import { useEffect, useRef, useState } from "react";
import api from "./api";

// ---------------- Custom icons per alert kind (SVG → data URL) ----------------
const svgIcon = (color, path) => {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'>
    <rect width='64' height='64' rx='14' fill='#09090b'/>
    <circle cx='32' cy='32' r='22' fill='${color}' fill-opacity='0.18'/>
    <g stroke='${color}' stroke-width='3' stroke-linecap='round' stroke-linejoin='round' fill='none'>${path}</g>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

export const ALERT_ICONS = {
  proximity: svgIcon("#007AFF",
    "<path d='M32 18l8 14h-6v12h-4V32h-6z' fill='#007AFF'/><circle cx='32' cy='32' r='18'/>"),
  geofence_enter: svgIcon("#34C759",
    "<circle cx='32' cy='32' r='14' stroke-dasharray='4 3'/><path d='M24 32l6 6 12-12'/>"),
  geofence_exit: svgIcon("#FF3B30",
    "<circle cx='32' cy='32' r='14' stroke-dasharray='4 3'/><path d='M26 26l12 12M38 26L26 38'/>"),
  checkin: svgIcon("#FFCC00",
    "<path d='M32 16v8M32 40v8M16 32h8M40 32h8'/><circle cx='32' cy='32' r='6' fill='#FFCC00'/>"),
  default: svgIcon("#a1a1aa",
    "<circle cx='32' cy='32' r='14'/><path d='M32 24v10M32 40v.5'/>"),
};

// ---------------- Audio (Web Audio API — no asset files needed) ----------------
let _audioCtx = null;
const getCtx = () => {
  if (typeof window === "undefined") return null;
  if (!_audioCtx) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    _audioCtx = new Ctx();
  }
  if (_audioCtx.state === "suspended") _audioCtx.resume();
  return _audioCtx;
};

// Tone helper: play a frequency for a duration with an envelope
const tone = (ctx, freq, startSec, durSec, gain = 0.28, type = "sine") => {
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  osc.connect(g);
  g.connect(ctx.destination);
  const t0 = ctx.currentTime + startSec;
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.02);
  g.gain.exponentialRampToValueAtTime(0.001, t0 + durSec);
  osc.start(t0);
  osc.stop(t0 + durSec + 0.05);
};

// "Genie chime" — a pleasant, magical, attention-grabbing cluster of bells.
// Plays an ascending arpeggio (C5–E5–G5–C6) plus a shimmering harmonic above,
// with a long-tail release that fades naturally. Used as the default alert sound.
const playGenieChime = (ctx, baseGain = 0.32) => {
  // C5=523.25, E5=659.25, G5=783.99, C6=1046.5, E6=1318.5, G6=1568
  const notes = [
    { f: 523.25, t: 0.00, d: 1.20 },
    { f: 659.25, t: 0.08, d: 1.10 },
    { f: 783.99, t: 0.16, d: 1.00 },
    { f: 1046.5, t: 0.26, d: 0.95 },
  ];
  notes.forEach((n) => {
    tone(ctx, n.f, n.t, n.d, baseGain, "sine");
    // Subtle harmonic two octaves up adds a "magical" shimmer
    tone(ctx, n.f * 4, n.t, n.d * 0.7, baseGain * 0.25, "sine");
  });
  // Soft sparkle at the end
  tone(ctx, 2093.0, 0.45, 0.6, baseGain * 0.18, "sine");
};

export const playAlertSound = (kind) => {
  const ctx = getCtx();
  if (!ctx) return;
  switch (kind) {
    case "proximity":
      // Two ascending pings (kept for proximity-specific notifications)
      tone(ctx, 880, 0, 0.22, 0.30);
      tone(ctx, 1320, 0.18, 0.30, 0.30);
      break;
    case "geofence_enter":
      // Friendly rising arpeggio
      tone(ctx, 660, 0, 0.18, 0.28);
      tone(ctx, 880, 0.10, 0.22, 0.28);
      tone(ctx, 1175, 0.22, 0.40, 0.30);
      break;
    case "geofence_exit":
      // Warning — descending pair (still pleasant, slightly more urgent)
      tone(ctx, 880, 0, 0.18, 0.30, "triangle");
      tone(ctx, 523, 0.18, 0.40, 0.30, "triangle");
      break;
    case "checkin":
      // Single warm bell
      tone(ctx, 988, 0, 0.50, 0.22, "sine");
      break;
    default:
      // Genie chime — pleasant, magical, attention-grabbing
      playGenieChime(ctx);
  }
};

// ---------------- Public hooks/funcs ----------------
export function useBrowserNotifications() {
  const [permission, setPermission] = useState(
    typeof window !== "undefined" && "Notification" in window ? Notification.permission : "unsupported"
  );

  const request = async () => {
    if (!("Notification" in window)) return "unsupported";
    if (Notification.permission === "granted") {
      setPermission("granted");
      // unlock audio context on user gesture
      getCtx();
      return "granted";
    }
    const p = await Notification.requestPermission();
    setPermission(p);
    if (p === "granted") getCtx();
    return p;
  };

  return { permission, request };
}

export function notify(title, body, { tag, kind } = {}) {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  const icon = ALERT_ICONS[kind] || ALERT_ICONS.default;
  try {
    const n = new Notification(title, {
      body,
      tag: tag || `proximity-${Date.now()}`,
      icon,
      badge: icon,
      silent: true, // we play our own custom sound below
    });
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch {}
  // Respect the user's "Alert sound" preference (Settings → Notifications).
  // Default is ON — the setting is stored as "true"/"false"; missing key = on.
  let soundOn = true;
  try {
    const raw = localStorage.getItem("proximity:alertSound");
    if (raw != null) soundOn = raw === "true";
  } catch {}
  if (soundOn) playAlertSound(kind);
}

// Polls /api/alerts and fires per-kind notifications for any new ids
export function useAlertNotifier({ enabled = true, intervalMs = 8000 } = {}) {
  const seen = useRef(new Set());
  const primed = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const tick = async () => {
      try {
        const r = await api.get("/alerts");
        const list = r.data || [];
        if (!primed.current) {
          list.forEach((a) => seen.current.add(a.alert_id));
          primed.current = true;
          return;
        }
        for (const a of list) {
          if (!seen.current.has(a.alert_id)) {
            seen.current.add(a.alert_id);
            notify(a.title, a.message, { tag: a.alert_id, kind: a.kind });
          }
        }
      } catch {}
    };

    tick();
    const t = setInterval(() => { if (!cancelled) tick(); }, intervalMs);
    return () => { cancelled = true; clearInterval(t); };
  }, [enabled, intervalMs]);
}
